import {
  DeleteOutlined,
  DownOutlined,
  EditOutlined,
  EllipsisOutlined,
  RightOutlined,
} from "@ant-design/icons";
import type { MenuProps } from "antd";
import clsx from "clsx";
import {
  useCallback,
  useState,
  type ChangeEvent,
  type KeyboardEvent,
  type MouseEvent,
} from "react";
import BDropdown from "../BDropdown/BDropdown";
import BEmpty from "../BEmpty/BEmpty";
import BInput from "../BInput/BInput";
import "./BHistoryList.scss";

export type BHistoryListItem = {
  id: string;
  title: string;
};

export type BHistoryListProps = {
  items: BHistoryListItem[];
  activeId?: string | null;
  onSelect?: (id: string) => void;
  onRename?: (id: string, name: string) => void;
  onDeleteRequest?: (id: string) => void;
  title?: string;
  collapsed?: boolean;
  defaultCollapsed?: boolean;
  onCollapsedChange?: (collapsed: boolean) => void;
  emptyDescription?: string;
  className?: string;
};

const BHistoryList = ({
  items,
  activeId,
  onSelect,
  onRename,
  onDeleteRequest,
  title = "Cuộc trò chuyện",
  collapsed,
  defaultCollapsed = false,
  onCollapsedChange,
  emptyDescription = "Chưa có chat",
  className,
}: BHistoryListProps) => {
  const isCollapsedControlled = collapsed !== undefined;
  const [internalCollapsed, setInternalCollapsed] = useState(defaultCollapsed);
  const [renamingId, setRenamingId] = useState<string | null>(null);
  const [renameValue, setRenameValue] = useState("");
  const [menuOpenId, setMenuOpenId] = useState<string | null>(null);
  const isCollapsed = isCollapsedControlled ? collapsed : internalCollapsed;
  const showActions = Boolean(onRename || onDeleteRequest);

  const handleToggleCollapse = useCallback(() => {
    const next = !isCollapsed;
    if (!isCollapsedControlled) {
      setInternalCollapsed(next);
    }
    onCollapsedChange?.(next);
  }, [isCollapsed, isCollapsedControlled, onCollapsedChange]);

  const handleSelect = useCallback(
    (event: MouseEvent<HTMLButtonElement>) => {
      const id = event.currentTarget.dataset.id;
      if (id) {
        onSelect?.(id);
      }
    },
    [onSelect],
  );

  const startRenaming = useCallback(
    (id: string) => {
      const item = items.find((entry) => entry.id === id);
      if (!item) {
        return;
      }
      setRenamingId(id);
      setRenameValue(item.title);
    },
    [items],
  );

  const cancelRename = useCallback(() => {
    setRenamingId(null);
    setRenameValue("");
  }, []);

  const commitRename = useCallback(() => {
    if (!renamingId) {
      return;
    }
    const trimmedName = renameValue.trim();
    if (trimmedName) {
      onRename?.(renamingId, trimmedName);
    }
    setRenamingId(null);
    setRenameValue("");
  }, [onRename, renameValue, renamingId]);

  const handleRenameKeyDown = useCallback(
    (event: KeyboardEvent<HTMLInputElement>) => {
      if (event.key === "Enter") {
        event.preventDefault();
        commitRename();
      } else if (event.key === "Escape") {
        event.preventDefault();
        cancelRename();
      }
    },
    [cancelRename, commitRename],
  );

  const handleRenameValueChange = useCallback((event: ChangeEvent<HTMLInputElement>) => {
    setRenameValue(event.target.value);
  }, []);

  const getMenuItems = useCallback((): MenuProps["items"] => {
    const menuItems: NonNullable<MenuProps["items"]> = [];

    if (onRename) {
      menuItems.push({
        key: "rename",
        label: "Đổi tên",
        icon: <EditOutlined />,
      });
    }

    if (onDeleteRequest) {
      menuItems.push({
        key: "delete",
        label: "Xóa",
        icon: <DeleteOutlined />,
        danger: true,
      });
    }

    return menuItems;
  }, [onDeleteRequest, onRename]);

  const handleMenuClick = useCallback(
    (itemId: string): MenuProps["onClick"] =>
      ({ key, domEvent }) => {
        domEvent.stopPropagation();
        setMenuOpenId(null);
        if (key === "rename") {
          startRenaming(itemId);
          return;
        }
        if (key === "delete") {
          onDeleteRequest?.(itemId);
        }
      },
    [onDeleteRequest, startRenaming],
  );

  const handleMenuButtonClick = useCallback((event: MouseEvent<HTMLButtonElement>) => {
    event.stopPropagation();
  }, []);

  const handleMenuOpenChange = useCallback((itemId: string, open: boolean) => {
    setMenuOpenId(open ? itemId : null);
  }, []);

  const getPopupContainer = useCallback(() => document.body, []);

  return (
    <div className={clsx("b-history-list", className)}>
      <div className="b-history-list__header">
        <span className="b-history-list__heading">{title}</span>
        <button
          type="button"
          className="b-history-list__collapse"
          aria-label={isCollapsed ? "Mở rộng chat" : "Thu gọn chat"}
          aria-expanded={!isCollapsed}
          onClick={handleToggleCollapse}
        >
          {isCollapsed ? <RightOutlined /> : <DownOutlined />}
        </button>
      </div>

      {!isCollapsed ? (
        items.length === 0 ? (
          <BEmpty description={emptyDescription} />
        ) : (
          <ul className="b-history-list__list">
            {items.map((item) => {
              const isActive = activeId === item.id;
              const isRenaming = renamingId === item.id;

              if (isRenaming) {
                return (
                  <li key={item.id} className="b-history-list__row">
                    <div className="b-history-list__item-wrap b-history-list__item-wrap--editing">
                      <BInput
                        autoFocus
                        className="b-history-list__name-input"
                        value={renameValue}
                        onChange={handleRenameValueChange}
                        onKeyDown={handleRenameKeyDown}
                        onBlur={commitRename}
                        aria-label="Tên cuộc trò chuyện"
                      />
                    </div>
                  </li>
                );
              }

              const isMenuOpen = menuOpenId === item.id;

              return (
                <li key={item.id} className="b-history-list__row">
                  <div
                    className={clsx("b-history-list__item-wrap", {
                      "b-history-list__item-wrap--active": isActive,
                      "b-history-list__item-wrap--menu-open": isMenuOpen,
                    })}
                  >
                    <button
                      type="button"
                      className={clsx("b-history-list__item", {
                        "b-history-list__item--active": isActive,
                      })}
                      data-id={item.id}
                      data-active={isActive || undefined}
                      onClick={handleSelect}
                    >
                      <span className="b-history-list__name">{item.title}</span>
                    </button>

                    {showActions ? (
                      <div className="b-history-list__actions">
                        <BDropdown
                          trigger={["click"]}
                          placement="bottomRight"
                          open={isMenuOpen}
                          onOpenChange={(open) => handleMenuOpenChange(item.id, open)}
                          getPopupContainer={getPopupContainer}
                          menu={{
                            items: getMenuItems(),
                            onClick: handleMenuClick(item.id),
                          }}
                          overlayClassName="b-history-list__menu-overlay"
                          overlayStyle={{ zIndex: 1200 }}
                        >
                          <button
                            type="button"
                            className="b-history-list__menu-btn"
                            aria-label="Tùy chọn cuộc trò chuyện"
                            onClick={handleMenuButtonClick}
                          >
                            <EllipsisOutlined />
                          </button>
                        </BDropdown>
                      </div>
                    ) : null}
                  </div>
                </li>
              );
            })}
          </ul>
        )
      ) : null}
    </div>
  );
};

export default BHistoryList;
